/**
 * CDO (Class Default Object) - default object creation and caching for serialization.
 */

import { getClassForDeserialization } from './clsAlias.js';
import { PROPERTY_METADATA_KEY } from './decorator.js';
import { loadPrefabTemplateInstance } from './prefab.js';
import { PrefabManager } from './prefabManager.js';

import type { PropertyMetadata } from './decorator.js';
import type { Constructor } from '../../systems/ClassRegistry.js';


interface PrefabTemplateCacheEntry {
  cdo: any;
  prefabData: any;
}

const cdoSpecializations = new Map<Function, any>();
const cdoCache = new Map<Function, any>();
const prefabTemplateCache = new Map<string, PrefabTemplateCacheEntry>();

// for unit test purposes
export function clearCdoCache(): void {
  cdoCache.clear();
  prefabTemplateCache.clear();
}

export function registerCdoSpecialization<T>(cls: Constructor<T>, cdo: T): void {
  cdoSpecializations.set(cls, cdo);
}

export function getClassDefaultObject<T>(cls: Constructor<T>): T | Record<string, any> | null {
  if (!cls) return null;

  if (cdoSpecializations.has(cls)) {
    return cdoSpecializations.get(cls);
  }

  if (cdoCache.has(cls)) {
    return cdoCache.get(cls);
  }

  const cdo = cls.length === 0 ? new cls() : getNonDefaultConstructableClassDefaults(cls.prototype);
  cdoCache.set(cls, cdo);

  return cdo;
}

// get a dictionary of fields marked with `default` in @property fields for those class that can't be default constructed, as in `new cls()`
function getNonDefaultConstructableClassDefaults(prototype: any): Record<string, any> {
  const properties = getAllPropertiesFromProto(prototype);
  const defaults: Record<string, any> = {};
  properties.forEach((meta, key) => {
    const resolvedDefault = resolveMetadataDefault(meta);
    if (resolvedDefault !== undefined) {
      defaults[key] = resolvedDefault;
    }
  });
  return defaults;
}

function resolveMetadataDefault(meta: PropertyMetadata): unknown {
  return meta.default;
}

export function getAllPropertiesFromProto(proto: any): Map<string, PropertyMetadata> {
  const keys = new Map<string, PropertyMetadata>();
  while (proto && proto !== Object.prototype) {
    const meta: Record<string, PropertyMetadata> = Reflect.getOwnMetadata(PROPERTY_METADATA_KEY, proto) ?? {};
    Object.keys(meta).forEach((key) => {
      if (!keys.has(key)) keys.set(key, meta[key]);
    });
    proto = Object.getPrototypeOf(proto);
  }

  return keys;
}

function _getPrefabTemplate(prefabPath: string): any | null {
  if (prefabTemplateCache.has(prefabPath)) {
    const entry = prefabTemplateCache.get(prefabPath)!;
    if (entry.prefabData === PrefabManager.getPrefabData(prefabPath)) {
      return entry.cdo;
    }
    // prefab data has changed, invalidate cache
    console.log(`Prefab data changed, invalidating cache for path: ${prefabPath}`);
    prefabTemplateCache.delete(prefabPath);
  }
  const template = loadPrefabTemplateInstance(prefabPath);
  if (template) {
    prefabTemplateCache.set(prefabPath, { cdo: template, prefabData: PrefabManager.getPrefabDataChecked(prefabPath) });
  }
  return template;
}

export function getDefaultObjectFromName(clsNameOrPrefabPath: string): any | null {
  if (PrefabManager.getPrefabData(clsNameOrPrefabPath)) {
    return _getPrefabTemplate(clsNameOrPrefabPath);
  }
  const cls = getClassForDeserialization(clsNameOrPrefabPath);
  if (cls) {
    return getClassDefaultObject(cls);
  }
  return null;
}

export function getDefaultObject(instance: any): any | null {
  if (!instance) return null;

  const prefabPath = instance.$prefab?.$path;
  if (prefabPath) {
    return _getPrefabTemplate(prefabPath);
  }

  return getClassDefaultObject(instance.constructor);
}